import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import { API_PATH } from "@/constants/config";
import { request } from "@/helper/http";
import { type SystemSettingServer, type SystemSettingTwoFa } from "@/types/systemSetting";

export const getSystemSettings = (): Promise<SystemSettingServer> => {
  return request({
    method: "get",
    url: API_PATH.SYSTEM_SETTINGS,
  });
};

export const getSystemSettingsTwoFa = (): Promise<SystemSettingTwoFa> =>
  request({
    method: "get",
    url: API_PATH.SYSTEM_SETTINGS_TWO_FA,
  });

export const updateSystemSettings = (payload: Partial<SystemSettingServer>): Promise<SystemSettingServer> =>
  request({
    method: "patch",
    url: API_PATH.SYSTEM_SETTINGS,
    data: payload,
  });

export const update2FaSystemSetting = (
  payload: Pick<SystemSettingTwoFa, "enable_2fa">
): Promise<SystemSettingTwoFa> => {
  const url = payload.enable_2fa ? API_PATH.SYSTEM_SETTINGS_TWO_FA_ENABLE : API_PATH.SYSTEM_SETTINGS_TWO_FA_DISABLE;

  return request({
    method: "post",
    url,
  });
};

export const systemSettingApi = createApi({
  reducerPath: "systemSettingApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["twoFaSystemSetting"],
  endpoints: (builder) => ({
    get2FaSystemSettings: builder.query({
      queryFn: async () => {
        try {
          const data = await getSystemSettingsTwoFa();
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["twoFaSystemSetting"],
    }),
    update2FaSystemSetting: builder.mutation({
      queryFn: async (payload: SystemSettingTwoFa) => {
        try {
          const data = await update2FaSystemSetting(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          systemSettingApi.util.updateQueryData("get2FaSystemSettings", {}, (draft) => {
            if (draft) {
              Object.assign(draft, arg);
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const { useGet2FaSystemSettingsQuery, useUpdate2FaSystemSettingMutation } = systemSettingApi;
